/**
 * Phase 19 — the rule list as the editor edits it, pure: a rule's conditions
 * are one ALL/ANY group of conditions and NOTs — the lines of the Flow
 * Optimizer's own conditions list (its view items, 12/10/2026; hour rules also
 * hour-tag lines, the Flow Optimizer's own tag node); a stored rule
 * nested deeper keeps its tree untouched and is shown read-only. New rules
 * start from the surface's starter condition over the formula's first range
 * (inherited, as a Flow Optimizer rule inherits its formula's window), on its
 * first action that is not Exclude (Exclude is the default "Not enough data"
 * rule's — Phase 22).
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_EXCLUDE_ACTION,
  DROP_GEN_FIRST_RANGE,
  DROP_GEN_RULE_LIMITS,
  type DropGenRule,
  type DropGenRuleHit,
  type DropGenRuleNode,
  NOT_ENOUGH_DATA_ID,
  newRule,
} from '@trafficpulse/shared';

import type { ExcludeViewItem, LeafRuleNode } from '@/features/flow-optimizer/utils/exclude-view-model';

import { RULE_SURFACES, type RuleSurface } from './rule-surfaces';

export type RuleCondition = Extract<DropGenRuleNode, { kind: 'condition' }>;
/** A rule's conditions as the Flow Optimizer's list draws them. */
export interface RuleLines {
  op: 'AND' | 'OR';
  items: ExcludeViewItem[];
}

const leaf = (c: RuleCondition) => c as unknown as LeafRuleNode;

type Line = Extract<DropGenRuleNode, { kind: 'condition' | 'ranking' | 'tag' }>;
const isLine = (n: DropGenRuleNode): n is Line => n.kind === 'condition' || n.kind === 'ranking' || n.kind === 'tag';

function itemOf(node: DropGenRuleNode, i: number): ExcludeViewItem | null {
  if (isLine(node)) return { kind: 'leaf', id: `c${i}`, node: leaf(node as RuleCondition) };
  if (node.kind === 'not' && isLine(node.child)) return { kind: 'not', id: `c${i}`, child: leaf(node.child as RuleCondition) };
  return null;
}

/** The rule's lines (conditions and NOTs of one group), else null (nested deeper — kept as saved). */
export function linesOf(when: DropGenRuleNode): RuleLines | null {
  const children = when.kind === 'group' ? when.children : [when];
  const items = children.map(itemOf);
  if (items.some((i) => i === null)) return null;
  return { op: when.kind === 'group' ? when.op : 'AND', items: items as ExcludeViewItem[] };
}

/** What goes back: a plain threshold, a raw-score ranking or an hour tag — never a compare or a pool scope. */
const conditionOf = (n: LeafRuleNode): Line => {
  if (n.kind === 'tag') return { kind: 'tag', tagName: n.tagName, ...(n.negate ? { negate: true } : {}) };
  if (n.kind === 'ranking')
    return { kind: 'ranking', metric: 'rawScore', op: n.op, ...(n.percent !== undefined ? { percent: n.percent } : {}) };
  const c = n as unknown as RuleCondition;
  return { kind: 'condition', metric: c.metric, op: c.op, value: c.value };
};

export const whenOf = (lines: RuleLines): DropGenRuleNode => ({
  kind: 'group',
  op: lines.op,
  children: lines.items.map((it) => (it.kind === 'leaf' ? conditionOf(it.node) : { kind: 'not', child: conditionOf(it.child) })),
});

/** A new line: the surface's starter condition, plain or inside a NOT. */
export function newLine(surface: RuleSurface, not: boolean, i: number): ExcludeViewItem {
  const node = leaf({ kind: 'condition', ...RULE_SURFACES[surface].starter });
  return not ? { kind: 'not', id: `c${i}`, child: node } : { kind: 'leaf', id: `c${i}`, node };
}

export function addRule<A extends string>(rules: DropGenRule<A>[], surface: RuleSurface): DropGenRule<A>[] {
  if (rules.length >= DROP_GEN_RULE_LIMITS.rules) return rules;
  const { actions, starter } = RULE_SURFACES[surface];
  const action = actions.find((a) => a !== DROP_GEN_EXCLUDE_ACTION) ?? actions[0];
  const rule = newRule(action as A, DROP_GEN_FIRST_RANGE, starter);
  return [...rules, { ...rule, name: `Rule ${rules.length + 1}` }];
}

export const patchRule = <A extends string>(rules: DropGenRule<A>[], id: string, patch: Partial<DropGenRule<A>>) =>
  rules.map((r) => (r.id === id ? { ...r, ...patch } : r));

export const removeRule = <A extends string>(rules: DropGenRule<A>[], id: string) => rules.filter((r) => r.id !== id);

/** The exclude rule that took an item out of the ranking (its words say why), else undefined. */
export const excludeHit = (hits: DropGenRuleHit[] | undefined) =>
  (hits ?? []).find((h) => h.state === 'fired' && h.action === DROP_GEN_EXCLUDE_ACTION);

/** The default "Not enough data" rule's number (Unique visits < N), else null — the old floor's place. */
export function notEnoughDataValue(rules: DropGenRule[] | undefined): number | null {
  const rule = (rules ?? []).find((r) => r.id === NOT_ENOUGH_DATA_ID && r.enabled);
  const line = rule ? linesOf(rule.when)?.items[0] : undefined;
  const first = line?.kind === 'leaf' ? (line.node as unknown as RuleCondition) : undefined;
  return first && first.metric === 'uniqueVisits' && first.op === '<' ? first.value : null;
}

/** "excludes under 200 visits · 2 rules · 1 off" — the step summary's words. */
export function rulesSummary(rules: DropGenRule[] | undefined): string | null {
  const list = rules ?? [];
  const floor = notEnoughDataValue(list);
  const others = list.filter((r) => !(r.id === NOT_ENOUGH_DATA_ID && floor !== null));
  const off = others.filter((r) => !r.enabled).length;
  const parts = [
    floor !== null ? `excludes under ${floor.toLocaleString('en-US')} visits` : null,
    others.length > 0 ? `${others.length} rule${others.length > 1 ? 's' : ''}${off > 0 ? ` · ${off} off` : ''}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
